import { useEffect, useRef, useCallback, useState } from 'react';
import { GOOGLE_MAPS_CONFIG } from '../utils/constants';

/**
 * Google Maps 초기화 및 관리 훅
 * LocationSection과 CheckDetails에서 공통으로 사용
 * 
 * @param {string} address - 지도에 표시할 주소
 * @returns {object} { mapRef, mapError, overlayRef }
 */
export const useGoogleMap = (address, coordinates = null) => {
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const latitude = coordinates?.lat;
  const longitude = coordinates?.lng;

  const initializeMap = useCallback(async () => {
    if (!mapRef.current || !window.google || !window.google.maps) return;
    if (!address) return;

    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0]);
          } else {
            reject(new Error('주소를 찾을 수 없습니다.'));
          }
        });
      });

      const mapCenter = {
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng(),
      };
      const selectedMapCenter =
        Number.isFinite(latitude) &&
        Number.isFinite(longitude)
          ? { lat: latitude, lng: longitude }
          : mapCenter;

      const map = new window.google.maps.Map(mapRef.current, {
        center: selectedMapCenter,
        zoom: GOOGLE_MAPS_CONFIG.DEFAULT_ZOOM,
        disableDefaultUI: true,
        zoomControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        // 지도 하단 'Keyboard shortcuts' 컨트롤 제거 (키보드 조작도 함께 비활성화됨)
        keyboardShortcuts: false,
      });

      // 커스텀 오버레이로 "Meeting point" 표시
      class MeetingPointOverlay extends window.google.maps.OverlayView {
        constructor(position) {
          super();
          this.position = position;
          this.container = document.createElement('div');
          this.container.style.position = 'absolute';
          this.container.style.display = 'flex';
          this.container.style.flexDirection = 'column';
          this.container.style.alignItems = 'center';
          this.container.style.transform = 'translate(-50%, -100%)';
          this.container.style.pointerEvents = 'none';

          const iconWrapper = document.createElement('div');
          iconWrapper.style.width = '26px';
          iconWrapper.style.height = '26px';
          iconWrapper.style.display = 'flex';
          iconWrapper.style.alignItems = 'center';
          iconWrapper.style.justifyContent = 'center';
          iconWrapper.innerHTML = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M26.2838 13.8255C26.2838 20.3491 16.4983 27.96 16.4983 27.96C16.4983 27.96 6.71289 20.3491 6.71289 13.8255C6.71289 8.49568 11.1685 4.04004 16.4983 4.04004C21.8282 4.04004 26.2838 8.49568 26.2838 13.8255Z" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M16.4962 17.0874C18.2977 17.0874 19.758 15.627 19.758 13.8255C19.758 12.0241 18.2977 10.5637 16.4962 10.5637C14.6947 10.5637 13.2344 12.0241 13.2344 13.8255C13.2344 15.627 14.6947 17.0874 16.4962 17.0874Z" fill="#F9F7F4"/>
          </svg>`;

          const label = document.createElement('div');
          label.style.marginTop = '2px';
          label.style.fontSize = '12px';
          label.style.fontWeight = '600';
          label.style.lineHeight = '1.4';
          label.style.color = '#171410';
          label.textContent = GOOGLE_MAPS_CONFIG.MEETING_POINT_LABEL;

          this.container.appendChild(iconWrapper);
          this.container.appendChild(label);
        }

        onAdd() {
          const panes = this.getPanes();
          panes.overlayMouseTarget.appendChild(this.container);
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;
          const position = projection.fromLatLngToDivPixel(this.position);
          if (position && this.container) {
            this.container.style.left = `${position.x}px`;
            this.container.style.top = `${position.y}px`;
          }
        }

        onRemove() {
          if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
          }
        }
      }

      const overlay = new MeetingPointOverlay(
        new window.google.maps.LatLng(
          selectedMapCenter.lat,
          selectedMapCenter.lng,
        )
      );
      overlay.setMap(map);
      overlayRef.current = overlay;
      setMapError(null);
    } catch (error) {
      console.error('지도 초기화 실패:', error);
      setMapError('주소를 찾을 수 없습니다.');
    }
  }, [address, latitude, longitude]);

  useEffect(() => {
    if (!address) return;

    const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    if (!API_KEY) {
      setMapError('지도 API 키가 설정되지 않았습니다.');
      return;
    }

    const waitForGoogleMaps = () =>
      new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 100;
        const timer = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.importLibrary) {
            clearInterval(timer);
            resolve();
            return;
          }

          attempts += 1;
          if (attempts >= maxAttempts) {
            clearInterval(timer);
            reject(new Error('Google Maps SDK load timeout'));
          }
        }, 100);
      });

    const ensureMapSdk = async () => {
      try {
        if (window.google && window.google.maps && window.google.maps.importLibrary) {
          initializeMap();
          return;
        }

        let script = document.querySelector('script[data-google-maps-sdk="true"]');

        if (!script) {
          script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places,marker`;
          script.async = true;
          script.defer = true;
          script.setAttribute('data-google-maps-sdk', 'true');
          script.onerror = () => {
            setMapError('Failed to load Google Maps.');
          };
          document.head.appendChild(script);
        }

        await waitForGoogleMaps();
        initializeMap();
      } catch (error) {
        console.error('Google Maps SDK load failed:', error);
        setMapError('Failed to load Google Maps.');
      }
    };

    ensureMapSdk();

    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [initializeMap, address]);

  return { mapRef, mapError, overlayRef };
};
